import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ChevronDown, ChevronRight, Database, AlertTriangle, ArrowDown, Slash, Shield, ShieldOff, ExternalLink, Tool, GitMerge, Play, Zap, Check, X, Terminal, Loader, Navigation, Watch, Activity } from "react-feather";
import { FunctionResult, SQLResult, AttachmentResult, ThoughtResult } from "../../utils/types";
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { formatDate } from "../../utils/time";

import "./ChatMessage.scss";
import { InternalLink } from "../internal-link/InternalLink";
import { useMobileDevice } from "../../utils/hooks";
import { MiniEditor } from "../editor/MiniEditor";
import API from "../../utils/api";

export interface ChatMessageEntry {
  unique_id?: string,
  name?: string,
  avatar?: string,
  app?: boolean,
  message?: string,
  thoughts?: ThoughtResult[] | null,
  functions?: FunctionResult[] | null,
  attachments?: AttachmentResult[] | null,
  sql?: SQLResult | null,
  notification?: string,
  is_text_warned?: boolean,
  is_text_filtered?: boolean,
  is_text_blocked?: boolean,
  status?: 'pending' | 'complete' | null,
  created_at?: string | null,
  onExpand?: () => void
};

export default function ChatMessage({
  unique_id,
  name = "User",
  avatar,
  app = false,
  message = "",
  thoughts = [],
  functions = [],
  attachments = [],
  sql,
  notification = "",
  is_text_warned = false,
  is_text_filtered = false,
  is_text_blocked = false,
  status = null,
  created_at = null,
  onExpand = () => {}
} : ChatMessageEntry) {

  const isMobileDevice = useMobileDevice();
  const [ isExpandedSql, setIsExpandedSql ] = useState(false);
  const [ isExpandedThoughts, setIsExpandedThoughts ] = useState(false);
  const [ expandedFunctions, setExpandedFunctions ] = useState<number[]>([]);
  const time = new Date(created_at || Date.now());

  const showAttachments = (attachments || []).filter(a => a.status === 'pending' || a.value);

  const showTypingIndicator = (
    status === 'pending' &&
    !message &&
    !thoughts?.find(t => t.status === 'pending') &&
    !functions?.find(f => f.status === 'pending') && 
    !attachments?.find(a => a.status === 'pending')
  );

  const markdown = useMemo(() => {
    return (
      <ReactMarkdown 
        rehypePlugins={[]}
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />,
          table: ({node, ...props}) => <table {...props} />,
        }}
      >
        {message}
      </ReactMarkdown>
    );
  }, [message]);

  const notificationMarkdown = useMemo(() => {
    return (
      <ReactMarkdown
        rehypePlugins={[]}
        remarkPlugins={[remarkGfm]}
      >
        {notification}
      </ReactMarkdown>
    );
  }, [notification]);

  //   .map((text, i) => {
  //     if (text.startsWith('http://') || text.startsWith('https://')) {
  //       if (text.endsWith('.')) {
  //         return <span key={i}><a href={text.slice(0, -1)} target="_blank">{text.slice(0, -1)}</a>.</span>;
  //       } else {
  //         return <span key={i}><a href={text} target="_blank">{text}</a></span>;
  //       }
  //     } else {
  //       return <span key={i}>{text}</span>;
  //     }
  //   });

  return (
    <div
      data-component="ChatMessage"
      data-is-mobile-device={isMobileDevice}
      data-is-app-response={!!app}
    >
      <div className="chat-body">
        {(
          avatar
            ? <div className="chat-avatar image">
                <img src={avatar} />
              </div>
            : <div className={app ? `chat-avatar app` : `chat-avatar`}>
                {(name[0] || "A").toUpperCase()}
              </div>
        )}
        <div className="chat-content">
          <div className="chat-message-container">
            <div className="chat-author">
              <div className="chat-author-name">
                {name}
                {app && <span className="app">AI</span>}
              </div>
              <div className="chat-time">{formatDate(time)}</div>
              {notification && (
                <div className="chat-notification">
                  <AlertTriangle />
                  Rate limited
                  <ArrowDown />
                  Decreased intelligence
                </div>
              )}
              {is_text_warned && (
                is_text_filtered 
                  ? <div className="chat-notification">
                      <ShieldOff />
                      Content filtered
                    </div>
                  : <div className="chat-notification">
                      <Shield />
                      Content warning
                    </div>
              )}
              {is_text_blocked && (
                <div className="chat-notification blocked">
                  <Slash />
                  Blocked
                </div>
              )}
            </div>
            {sql && (
              <div className="sql">
                <div className="query-container" onClick={() => {
                  setIsExpandedSql(!isExpandedSql);
                  onExpand();
                }}>
                  <span className="expand-icon">
                    {isExpandedSql ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                  <Database />
                  <pre className="query">{sql.query}</pre>
                </div>
                {isExpandedSql && (
                  <pre className="result">{JSON.stringify(sql.result, null, 2)}</pre>
                )}
              </div>
            )}
            {thoughts && thoughts.length > 0 && (
              <div className="function">
                <div className="function-header">
                  <div
                    className="function-header-info"
                    onClick={() => {
                      setIsExpandedThoughts(v => !v);
                      onExpand();
                    }}
                  >
                    <span className="function-name">
                      {thoughts[thoughts.length - 1].status === 'pending' && <Activity />}
                      {thoughts[thoughts.length - 1].status !== 'pending' && <Activity className="thought" />}
                      <span className={`name-text ${thoughts[thoughts.length - 1].status === 'pending' ? 'pending' : ''}`}>{thoughts[thoughts.length - 1].summary}</span>
                    </span>
                    <span className="expand-icon">
                      {isExpandedThoughts ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                  </div>
                </div>
                {isExpandedThoughts && (
                  <div className="function-body">
                    <div className="function-body-thought">
                      {thoughts.map((t, i) => {
                        return (
                          <div className="thought"key={i}>
                            <strong>{t.summary}</strong> {t.time > 100 ? `${(t.time / 1000).toFixed(2)} s` : ''}
                            <div>{t.content}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {functions && functions.length > 0 && (
              functions.map((f, i) => {
                let route = `/${f.url.replace(/^(https?):\/\/.*?\//gi, '')}`;
                let args = f.arguments;
                let result = f.result;
                try {
                  args = JSON.stringify(JSON.parse(args), null, 2);
                } catch (e) {
                  // do nothing
                }
                if (typeof result === 'string') {
                  try {
                    result = JSON.stringify(JSON.parse(result), null, 2);
                  } catch (e) {
                    // do nothing
                  }
                }
                const showResult = f.status !== 'pending' && !attachments?.find(a => a.id === f.id && a.status !== 'pending' && a.value);
                const isExpanded = expandedFunctions.includes(i);
                return (
                  <div className="function" key={i}>
                    <div className="function-header">
                      <div
                        className="function-header-info"
                        onClick={() => {
                          setExpandedFunctions(prev => 
                            isExpanded 
                              ? prev.filter(idx => idx !== i)
                              : [...prev, i]
                          );
                          onExpand();
                        }}
                      >
                        <span className="function-name">
                          {f.status === 'pending' && <Package />}
                          {f.status !== 'pending' && (f.has_error ? <X className="error" /> : <Check className="success" />)}
                          <span className={`name-text ${f.status === 'pending' ? 'pending' : ''}`}>{f.title || `${f.method} ${route}`}</span>
                        </span>
                        <span className="expand-icon">
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </span>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="function-body">
                        <div className="function-body-info">
                          <Package />
                          <InternalLink to={f.package.url} target="_blank">
                            {f.package.name}/{f.package.version}
                          </InternalLink>
                        </div>
                        <div className="function-body-info">
                          <Tool />
                          <InternalLink to={`${f.package.url}/${f.filename}?method=${f.method}`} target="_blank">
                            {f.method} {route}
                          </InternalLink>
                        </div>
                        <MiniEditor
                          label="Arguments"
                          icon={Terminal}
                          copyable
                          value={args}
                          language="javascript"
                          readonly={true}
                          showLines={false}
                          display="embed"
                        />
                        {showResult && (
                          <MiniEditor
                            label={f.has_error ? "Error" : "Result"}
                            icon={f.has_error ? X : Check}
                            copyable
                            value={result}
                            type={f.has_error ? "error" : "success"}
                            language="javascript"
                            readonly={true}
                            showLines={false}
                            display="embed"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
            {showAttachments.length > 0 && (
              <div className="chat-attachments">
                {showAttachments.map((a, i) => (
                  <div className="chat-attachment" key={a.id || i}>
                    {a.status !== 'pending' && (
                      a.type?.split('/')[0] === 'image' && (
                        (a.value && '_base64' in a.value)
                          ? <img src={`data:image/png;base64,${a.value?._base64}`} />
                          : <img src={a.value?._url} />
                      )
                    )}
                    {a.status === 'pending' && <div className="pending" />}
                  </div>
                ))}
              </div>
            )}
            <div className={`chat-message ${status === 'pending' ? 'pending' : ''}`}>
              {markdown}
              {/* Deprecated for now: shows up in main chat box */}
              {/* notification && <div className="chat-notification">{notificationParts}</div> */}
            </div>
            {showTypingIndicator && (
              <div className="chat-typing">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            )}
            {status !== 'pending' && notification && (
              <div className="chat-notification-block">
                {notificationMarkdown}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};